import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
} from "@mui/material";
import { Block } from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { TimePicker } from "@mui/x-date-pickers/TimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import { createVenueBlock, fetchVenueBlocks } from "./api";
import type { Club, UpcomingTrainingDate, VenueBlock } from "./types";
import DialogHeader from "./DialogHeader";
import {
  blockOverlapsExisting,
  getRollingDateRange,
  isValidTimeRange,
  toDateString,
  toTimeString,
} from "./venueHireUtils";

interface BlockTimeDialogProps {
  club: Club | null;
  onClose: () => void;
  onSuccess: () => Promise<void> | void;
  onError: (message: string) => void;
}

type TrainingSessionOption = UpcomingTrainingDate & { key: string };

const BlockTimeDialog: React.FC<BlockTimeDialogProps> = ({
  club,
  onClose,
  onSuccess,
  onError,
}) => {
  const [date, setDate] = useState<Dayjs | null>(dayjs());
  const [startTime, setStartTime] = useState<Dayjs | null>(
    dayjs().hour(18).minute(0),
  );
  const [endTime, setEndTime] = useState<Dayjs | null>(
    dayjs().hour(20).minute(0),
  );
  const [reason, setReason] = useState("");
  const [existingBlocks, setExistingBlocks] = useState<VenueBlock[]>([]);
  const [selectedSessionKey, setSelectedSessionKey] = useState("");
  const [validationError, setValidationError] = useState("");
  const [creating, setCreating] = useState(false);

  const trainingSessions = useMemo((): TrainingSessionOption[] => {
    if (!club) return [];
    return club.training_groups.flatMap((group) =>
      (group.upcoming_training_dates ?? []).map((session) => ({
        ...session,
        key: `${group.group_name}-${session.training_date}`,
      })),
    );
  }, [club]);

  useEffect(() => {
    if (!club) return;
    setDate(dayjs());
    setStartTime(dayjs().hour(18).minute(0));
    setEndTime(dayjs().hour(20).minute(0));
    setReason("");
    setSelectedSessionKey("");
    setValidationError("");
    setExistingBlocks([]);

    const load = async () => {
      try {
        const range = getRollingDateRange(8);
        const data = await fetchVenueBlocks(club.id, range.from, range.to);
        setExistingBlocks(data);
      } catch {
        setExistingBlocks([]);
      }
    };
    load();
  }, [club]);

  useEffect(() => {
    if (!selectedSessionKey || !club) return;
    const session = trainingSessions.find((s) => s.key === selectedSessionKey);
    if (!session) return;

    const sessionStart = dayjs(session.training_date);
    setDate(sessionStart.startOf("day"));
    setStartTime(sessionStart.subtract(30, "minute"));
    setEndTime(sessionStart.add(2, "hour").add(30, "minute"));
    setReason(`Training: ${session.group_name}`);
  }, [selectedSessionKey, trainingSessions, club]);

  const validate = (): string | null => {
    if (!date || !startTime || !endTime) {
      return "Please select a date and time range.";
    }
    const dateStr = toDateString(date);
    const startStr = toTimeString(startTime);
    const endStr = toTimeString(endTime);
    if (!isValidTimeRange(startStr, endStr)) {
      return "End time must be after start time.";
    }
    if (blockOverlapsExisting(dateStr, startStr, endStr, existingBlocks)) {
      return "This block overlaps an existing blocked time on the same date.";
    }
    return null;
  };

  const handleCreate = async () => {
    if (!club || !date || !startTime || !endTime) return;
    const error = validate();
    if (error) {
      setValidationError(error);
      return;
    }
    try {
      setCreating(true);
      await createVenueBlock(club.id, {
        date: toDateString(date),
        start_time: toTimeString(startTime),
        end_time: toTimeString(endTime),
        reason: reason.trim() || undefined,
      });
      await onSuccess();
      onClose();
    } catch (err) {
      console.error("Error creating block:", err);
      onError("Failed to block time. Please try again.");
    } finally {
      setCreating(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog
        open={club != null}
        onClose={() => !creating && onClose()}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogHeader
          title="Block Time"
          subtitle={club?.name}
          icon={<Block color="warning" />}
          onClose={onClose}
          closeDisabled={creating}
        />
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ mt: 1 }}>
            {validationError && (
              <Alert severity="error">{validationError}</Alert>
            )}

            {trainingSessions.length > 0 && (
              <FormControl fullWidth>
                <InputLabel id="training-session-label">
                  Block from training session (optional)
                </InputLabel>
                <Select
                  labelId="training-session-label"
                  label="Block from training session (optional)"
                  value={selectedSessionKey}
                  onChange={(e) => setSelectedSessionKey(e.target.value)}
                  disabled={creating}
                >
                  <MenuItem value="">
                    <em>None — enter manually</em>
                  </MenuItem>
                  {trainingSessions.map((session) => (
                    <MenuItem key={session.key} value={session.key}>
                      {session.group_name} —{" "}
                      {dayjs(session.training_date).format("ddd D MMM, HH:mm")}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <DatePicker
              label="Date"
              value={date}
              onChange={setDate}
              disabled={creating}
              slotProps={{ textField: { fullWidth: true } }}
            />
            <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
              <TimePicker
                label="Start time"
                value={startTime}
                onChange={setStartTime}
                disabled={creating}
                slotProps={{ textField: { fullWidth: true } }}
              />
              <TimePicker
                label="End time"
                value={endTime}
                onChange={setEndTime}
                disabled={creating}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Stack>
            <TextField
              label="Reason (optional)"
              placeholder="e.g. Tuesday training session"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={creating}
              fullWidth
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose} disabled={creating}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={handleCreate}
            disabled={creating}
            startIcon={creating ? <CircularProgress size={20} /> : <Block />}
          >
            {creating ? "Blocking…" : "Block Time"}
          </Button>
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default BlockTimeDialog;
